
import { 
  LayoutDashboard, 
  FileText,
  Building,
  Users,
  FileCheck, 
  DollarSign, 
  AlertTriangle, 
  Calendar, 
  Settings,
  Wrench,
  UserCheck,
  FolderOpen,
  MessageSquare,
  Menu,
  Sparkles,
  CheckSquare,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { UserRole } from "@/types/auth";
import { useTarefasBadgeCount } from "@/hooks/useTarefas";

interface AppSidebarProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

interface MenuItem {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  requiredRole: UserRole;
  description?: string;
}

export function AppSidebar({ activeSection, setActiveSection, isCollapsed, setIsCollapsed }: AppSidebarProps) {
  const { 
    canAccessCadastros, 
    canAccessServicos, 
    canAccessEquipamentos, 
    canAccessFormasPagamento, 
    canAccessUsuarios,
    canAccessFinanceiro,
    canAccessAgenda,
    canAccessProjetos,
    canAccessContratos,
    canAccessOrcamentos,
    canAccessSinistros,
    canAccessPedidos,
    canAccessCrm,
    hasUserPermission
  } = useAuth();

  const getMenuItems = (): MenuItem[] => {
    const items: MenuItem[] = [
      { 
        id: "dashboard", 
        title: "Dashboard", 
        icon: LayoutDashboard, 
        requiredRole: 'viewer',
        description: 'Visão geral do sistema'
      },
      { 
        id: "tarefas", 
        title: "Tarefas", 
        icon: CheckSquare, 
        requiredRole: 'viewer',
        description: 'Kanban, calendário e lembretes do time'
      },
      { 
        id: "crm", 
        title: "CRM", 
        icon: MessageSquare, 
        requiredRole: 'viewer',
        description: 'Atendimento, Kanban e campanhas de WhatsApp'
      },
      { 
        id: "clientes", 
        title: "Clientes", 
        icon: Users, 
        requiredRole: 'viewer',
        description: 'Gestão de clientes'
      },
      { 
        id: "projetos", 
        title: "Projetos", 
        icon: FolderOpen, 
        requiredRole: 'viewer',
        description: 'Gestão de projetos'
      },
      { 
        id: "orcamentos", 
        title: "Orçamentos", 
        icon: FileText, 
        requiredRole: 'viewer',
        description: 'Gestão de orçamentos'
      },
      { 
        id: "pedidos", 
        title: "Pedidos", 
        icon: FileCheck, 
        requiredRole: 'viewer',
        description: 'Gestão de pedidos'
      },
      { 
        id: "contratos", 
        title: "Contratos", 
        icon: FileCheck, 
        requiredRole: 'viewer',
        description: 'Gestão de contratos'
      },
      { 
        id: "financeiro", 
        title: "Financeiro", 
        icon: DollarSign, 
        requiredRole: 'viewer',
        description: 'Gestão financeira'
      },
      { 
        id: "agenda", 
        title: "Agenda", 
        icon: Calendar, 
        requiredRole: 'viewer',
        description: 'Agenda e compromissos'
      },
      { 
        id: "pedidos_nao_renovados", 
        title: "Não renovados", 
        icon: FileCheck, 
        requiredRole: 'viewer',
        description: 'Pedidos sem renovação de seguro'
      },
      {
        id: "limpeza",
        title: "Limpeza",
        icon: Sparkles,
        requiredRole: 'viewer',
        description: 'Agendamento de limpeza (plano Proteção + Limpeza)'
      },
      { 
        id: "sinistros", 
        title: "Sinistros", 
        icon: AlertTriangle, 
        requiredRole: 'viewer',
        description: 'Gestão de sinistros'
      },
      { 
        id: "servicos", 
        title: "Serviços", 
        icon: Settings, 
        requiredRole: 'admin',
        description: 'Gestão de serviços (apenas admin)'
      },
      { 
        id: "usuarios", 
        title: "Usuários", 
        icon: UserCheck, 
        requiredRole: 'admin',
        description: 'Gestão de usuários (apenas admin)'
      },
      { 
        id: "cadastros", 
        title: "Cadastros", 
        icon: Building, 
        requiredRole: 'operator',
        description: 'Cadastros gerais (admin e operador)'
      },
    ];

    // Filtrar itens baseado nas permissões do usuário
    return items.filter(item => {
      // Verificar se o usuário tem a permissão necessária
      if (!hasUserPermission(item.requiredRole)) {
        return false;
      }

      // Verificações específicas para alguns itens
      switch (item.id) {
        case 'usuarios':
          return canAccessUsuarios();
        case 'financeiro':
          return canAccessFinanceiro();
        case 'agenda':
          return canAccessAgenda();
        case 'projetos':
          return canAccessProjetos();
        case 'contratos':
          return canAccessContratos();
        case 'orcamentos':
          return canAccessOrcamentos();
        case 'pedidos':
        case 'pedidos_nao_renovados':
        case 'limpeza':
          return canAccessPedidos();
        case 'crm':
          return canAccessCrm();
        case 'sinistros':
          return canAccessSinistros();
        case 'cadastros':
          return canAccessCadastros();
        case 'servicos':
          return canAccessServicos();
        default:
          // dashboard, clientes, tarefas, etc.
          return true;
      }
    });
  };

  const menuItems = getMenuItems();
  const { data: tarefasBadge = 0 } = useTarefasBadgeCount();

  return (
    <div className={`h-screen bg-gradient-to-b from-[#C9C9C9] to-[#252D30] border-r border-gray-200 flex flex-col fixed left-0 top-0 z-50 transition-all duration-300 ${
      isCollapsed ? 'w-20' : 'w-64'
    }`}>
      <div className={`${isCollapsed ? 'p-2' : 'p-3'}`}>
        <div className={`flex items-center justify-center relative ${isCollapsed ? 'mb-4' : 'mb-6'}`}>
          {!isCollapsed && (
            <img 
              src="/lovable-uploads/d667c6e3-25d0-4bcd-b305-1e54d4fa99ab.png" 
              alt="SunPro Logo" 
              className="w-40 h-40 object-contain"
            />
          )}
          {isCollapsed && (
            <img 
              src="/logo-sunpro-mini.png" 
              alt="SunPro Icon" 
              className="w-10 h-10 object-contain"
            />
          )}
          
          {/* Botão de toggle para minimizar/expandir */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="absolute -right-2 top-1/2 transform -translate-y-1/2 text-white/80 hover:text-white transition-all duration-200"
            title={isCollapsed ? "Expandir menu" : "Minimizar menu"}
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      <div className={`flex-1 ${isCollapsed ? 'px-2' : 'px-4'}`}>
        {!isCollapsed && (
          <h2 className="text-center text-grenn text-xs font-semibold mb-3">Menu Principal</h2>
        )}
        <div className="space-y-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`relative w-full text-left px-2.5 py-2 rounded-lg transition-all duration-200 ${
                activeSection === item.id 
                  ? "bg-white/20 text-white border-r-2 border-white shadow-lg" 
                  : "text-white/80 hover:bg-white/10 hover:text-white"
              }`}
              title={isCollapsed ? item.title : item.description}
            >
              <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''} w-full`}>
                <item.icon className={`${isCollapsed ? 'h-6 w-6' : 'h-5 w-5 mr-3'} shrink-0`} />
                {!isCollapsed && (
                  <span className="text-xs font-medium leading-tight flex-1">{item.title}</span>
                )}
                {item.id === "tarefas" && tarefasBadge > 0 && (
                  <span
                    className={`ml-auto text-[10px] font-bold rounded-full bg-red-500 text-white min-w-[18px] h-[18px] px-1 flex items-center justify-center ${
                      isCollapsed ? "absolute top-0.5 right-0.5" : ""
                    }`}
                  >
                    {tarefasBadge > 99 ? "99+" : tarefasBadge}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
